<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import { Card, CardContent, CardHeader, CardAction } from '$lib/components/ui/card/index.js'
  import PageContainer from '$lib/components/page-container.svelte'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import PlusIcon from '@lucide/svelte/icons/plus'
  import HomeIcon from '@lucide/svelte/icons/house'

  let { property, rooms = [] } = $props()

  function formatRent(cents) {
    const rm = cents / 100
    return 'RM ' + rm.toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  }

  function formatDateRange(startDate, endDate) {
    if (!startDate || !endDate) return '—'
    const fmt = new Intl.DateTimeFormat('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
    return `${fmt.format(new Date(startDate))} – ${fmt.format(new Date(endDate))}`
  }
</script>

<svelte:head>
  <title>Income Entry — MyRentalTax</title>
</svelte:head>

<PageContainer>
  {#snippet breadcrumb()}
    <span class="text-[11px] font-medium text-muted-foreground">Income Entry · Step 1 of 5</span>
  {/snippet}

  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div class="flex flex-col gap-1">
      <h1 class="text-xl sm:text-2xl font-bold text-zinc-900 m-0">Rooms / Rental Sources</h1>
      <p class="text-sm text-zinc-500 m-0">{property.name}{property.city ? ` · ${property.city}` : ''}</p>
    </div>
    <Button onclick={() => router.visit(`/properties/${property.id}/rooms/new`)} class="w-full sm:w-auto">
      <PlusIcon class="w-4 h-4" />
      Add Room
    </Button>
  </div>

  <!-- Progress row (desktop) -->
  <div class="hidden sm:flex items-center gap-3">
    <span class="text-xs font-medium text-muted-foreground">Step 1 of 5</span>
    <Progress value={1} max={5} class="flex-1" />
  </div>

  {#if rooms.length === 0}
    <div class="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <HomeIcon class="w-8 h-8 text-zinc-300" />
      <p class="text-sm text-zinc-500">No rooms added yet. Add your first room to get started.</p>
    </div>
  {:else}
    <div class="flex flex-col gap-3">
      {#each rooms as room (room.id)}
        <Card class="shadow-none border-zinc-200">
          <CardHeader class="px-5 py-3.5">
            <div class="flex items-center gap-2">
              <HomeIcon class="w-4 h-4 text-zinc-400 flex-shrink-0" />
              <span class="text-sm font-semibold text-zinc-900">{room.name}</span>
            </div>
            <CardAction>
              <Button variant="ghost" size="sm" onclick={() => router.visit(`/properties/${property.id}/rooms/${room.id}/edit`)} class="text-xs h-7 px-2.5">
                Edit
              </Button>
            </CardAction>
          </CardHeader>
          {#if room.income_entries && room.income_entries.length > 0}
            <CardContent class="px-5 pb-4 border-t border-zinc-100 pt-3 flex flex-col gap-2.5">
              {#each room.income_entries as entry, i (entry.id)}
                <div class="flex flex-col gap-0.5">
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-zinc-700">{entry.tenant_name || `Entry ${i + 1}`}</span>
                    <span class="font-medium text-zinc-900">{formatRent(entry.monthly_rent)}/mo</span>
                  </div>
                  <span class="text-xs text-zinc-400">{formatDateRange(entry.start_date, entry.end_date)}</span>
                </div>
              {/each}
            </CardContent>
          {/if}
        </Card>
      {/each}
    </div>
  {/if}

  {#snippet footer()}
    <Button variant="outline" onclick={() => router.visit('/properties')}>Back</Button>
    <div class="flex-1"></div>
    <Button onclick={() => router.visit(`/properties/${property.id}/property_finance`)}>Next: Property Finance</Button>
  {/snippet}
</PageContainer>
